import { useEffect, useState, useCallback } from 'react'
import type { ReactNode } from 'react'
import { createClient } from '@/lib/supabase/client'
import { useUser } from '@/contexts/UserContext'
import type { Theme } from './types'
import { DEFAULT_THEME } from './presets'
import { applyThemeToDom } from './domUtils'
import { ThemeContext } from './context'
import { useSignedUrl } from '@/lib/services/storage'

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { employee } = useUser()
  const [theme, setTheme] = useState<Theme>(DEFAULT_THEME)
  const [orgId, setOrgId] = useState<string | null>(null)
  const [employeeId, setEmployeeId] = useState<string | null>(null)
  const [isPersonalTheme, setIsPersonalTheme] = useState(false)

  const bgUrl = useSignedUrl(createClient(), theme.backgroundImage)

  useEffect(() => {
    document.documentElement.style.setProperty('--bg-image', bgUrl ? `url(${bgUrl})` : 'none')
  }, [bgUrl])

  useEffect(() => {
    const load = async () => {
      const supabase = createClient()

      const { data: orgData } = await supabase
        .from('organizations')
        .select('id, theme')
        .limit(1)
        .single()

      const raw = orgData?.theme as Partial<Theme> | null
      const orgTheme: Theme = raw
        ? { ...DEFAULT_THEME, ...raw, colors: { ...DEFAULT_THEME.colors, ...raw.colors } }
        : DEFAULT_THEME

      if (orgData) setOrgId(orgData.id)

      if (employee?.id) {
        setEmployeeId(employee.id)
        const { data: themeRow } = await supabase
          .from('employees_pii')
          .select('personal_theme')
          .eq('id', employee.id)
          .single()
        if (themeRow?.personal_theme) {
          const pt = themeRow.personal_theme as Partial<Theme>
          const personalTheme: Theme = { ...DEFAULT_THEME, ...pt, colors: { ...DEFAULT_THEME.colors, ...pt.colors } }
          setTheme(personalTheme)
          setIsPersonalTheme(true)
          applyThemeToDom(personalTheme)
          return
        }
      }

      setTheme(orgTheme)
      applyThemeToDom(orgTheme)
    }
    load()
  }, [employee?.id])

  const applyTheme = useCallback((t: Theme) => {
    setTheme(t)
    applyThemeToDom(t)
  }, [])

  const saveTheme = useCallback(async (t: Theme) => {
    if (!orgId) return
    const supabase = createClient()
    await supabase
      .from('organizations')
      .update({ theme: t })
      .eq('id', orgId)
    setTheme(t)
    applyThemeToDom(t)
  }, [orgId])

  const savePersonalTheme = useCallback(async (t: Theme | null) => {
    if (!employeeId) return
    const supabase = createClient()
    await supabase
      .from('employees_pii')
      .update({ personal_theme: t })
      .eq('id', employeeId)
    if (t) {
      setTheme(t)
      setIsPersonalTheme(true)
      applyThemeToDom(t)
    } else {
      setIsPersonalTheme(false)
      const { data: orgData } = await supabase.from('organizations').select('theme').limit(1).single()
      const rawTheme = orgData?.theme as Partial<Theme> | null
      const orgTheme = rawTheme
        ? { ...DEFAULT_THEME, ...rawTheme, colors: { ...DEFAULT_THEME.colors, ...rawTheme.colors } }
        : DEFAULT_THEME
      setTheme(orgTheme)
      applyThemeToDom(orgTheme)
    }
  }, [employeeId])

  return (
    <ThemeContext.Provider value={{ theme, orgId, applyTheme, saveTheme, savePersonalTheme, isPersonalTheme }}>
      <style>{`
        body::before {
          content: '';
          position: fixed;
          inset: 0;
          z-index: -1;
          background-image: var(--bg-image, none);
          background-size: cover;
          background-position: center;
          opacity: var(--bg-opacity, 0.05);
          pointer-events: none;
        }
      `}</style>
      {children}
    </ThemeContext.Provider>
  )
}
